import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Breadcrumbs from '../../components/BreadCrumbs';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import { getBlogCategories, getBlogTags, getBlogPosts } from '../../services/blogCategory';
import { getBlogTags as getTags } from '../../services/blogTag';
import { getBlogPosts as getPosts } from '../../services/blogPost';
import { toast } from 'react-toastify';
import { FaBlog, FaTags, FaFolder, FaEye, FaEdit, FaPlus } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const BlogDashboard = () => {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({
        totalCategories: 0,
        totalTags: 0,
        totalPosts: 0,
        publishedPosts: 0,
        draftPosts: 0,
        archivedPosts: 0,
        totalViews: 0,
        featuredPosts: 0
    });

    const fetchStats = async () => {
        setLoading(true);
        try {
            const [categoriesRes, tagsRes, postsRes] = await Promise.all([
                getBlogCategories(),
                getTags(),
                getPosts()
            ]);

            if (categoriesRes.status === 200) {
                setStats(prev => ({
                    ...prev,
                    totalCategories: categoriesRes.data.count
                }));
            }

            if (tagsRes.status === 200) {
                setStats(prev => ({
                    ...prev,
                    totalTags: tagsRes.data.count
                }));
            }

            if (postsRes.status === 200) {
                const posts = postsRes.data.results;
                const published = posts.filter(post => post.status === 'published').length;
                const draft = posts.filter(post => post.status === 'draft').length;
                const archived = posts.filter(post => post.status === 'archived').length;
                const totalViews = posts.reduce((sum, post) => sum + (post.views_count || 0), 0);
                const featured = posts.filter(post => post.is_featured).length;

                setStats(prev => ({
                    ...prev,
                    totalPosts: postsRes.data.count,
                    publishedPosts: published,
                    draftPosts: draft,
                    archivedPosts: archived,
                    totalViews: totalViews,
                    featuredPosts: featured
                }));
            }
        } catch (error) {
            console.error('Error fetching blog stats:', error);
            toast.error('Failed to fetch blog statistics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    const quickActions = [
        {
            title: 'Create New Post',
            description: 'Write and publish a new blog post',
            icon: <FaPlus className="text-2xl text-blue-600" />,
            link: '/blog/posts/create',
            color: 'bg-blue-50 border-blue-200 hover:bg-blue-100'
        },
        {
            title: 'Manage Categories',
            description: 'Organize posts with categories',
            icon: <FaFolder className="text-2xl text-green-600" />,
            link: '/blog/categories',
            color: 'bg-green-50 border-green-200 hover:bg-green-100'
        },
        {
            title: 'Manage Tags',
            description: 'Add and organize post tags',
            icon: <FaTags className="text-2xl text-purple-600" />,
            link: '/blog/tags',
            color: 'bg-purple-50 border-purple-200 hover:bg-purple-100'
        },
        {
            title: 'View All Posts',
            description: 'Browse and manage all blog posts',
            icon: <FaEdit className="text-2xl text-orange-600" />,
            link: '/blog/posts',
            color: 'bg-orange-50 border-orange-200 hover:bg-orange-100'
        }
    ];

    const statCards = [
        {
            title: 'Total Posts',
            count: stats.totalPosts,
            icon: <FaBlog className="text-2xl text-blue-600" />,
            color: 'bg-blue-50 border-blue-200'
        },
        {
            title: 'Published Posts',
            count: stats.publishedPosts,
            icon: <FaEye className="text-2xl text-green-600" />,
            color: 'bg-green-50 border-green-200'
        },
        {
            title: 'Draft Posts',
            count: stats.draftPosts,
            icon: <FaEdit className="text-2xl text-yellow-600" />,
            color: 'bg-yellow-50 border-yellow-200'
        },
        {
            title: 'Total Views',
            count: stats.totalViews.toLocaleString(),
            icon: <FaEye className="text-2xl text-purple-600" />,
            color: 'bg-purple-50 border-purple-200'
        },
        {
            title: 'Categories',
            count: stats.totalCategories,
            icon: <FaFolder className="text-2xl text-indigo-600" />,
            color: 'bg-indigo-50 border-indigo-200'
        },
        {
            title: 'Tags',
            count: stats.totalTags,
            icon: <FaTags className="text-2xl text-pink-600" />,
            color: 'bg-pink-50 border-pink-200'
        }
    ];

    return (
        <>
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Blog' },
            ]} />

            <div className="py-3">
                <div className="mb-6">
                    <StyledHeadingText text="Blog Dashboard" />
                    <p className="text-gray-600 mt-2">
                        Manage your blog content, categories, and tags
                    </p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
                    {statCards.map((card, index) => (
                        <motion.div
                            key={index}
                            whileHover={{ scale: 1.02 }}
                            className={`p-4 rounded-lg border ${card.color} transition-all duration-200`}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-gray-600">{card.title}</p>
                                    <p className="text-2xl font-bold text-gray-900">{card.count}</p>
                                </div>
                                <div className="p-2 rounded-full bg-white">
                                    {card.icon}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Quick Actions */}
                <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {quickActions.map((action, index) => (
                            <Link key={index} to={action.link}>
                                <motion.div
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className={`p-6 rounded-lg border-2 ${action.color} transition-all duration-200 cursor-pointer`}
                                >
                                    <div className="flex items-center space-x-4">
                                        <div className="p-3 rounded-full bg-white">
                                            {action.icon}
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-900">{action.title}</h4>
                                            <p className="text-sm text-gray-600">{action.description}</p>
                                        </div>
                                    </div>
                                </motion.div>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* Recent Activity or Additional Info */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Blog Overview</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="font-medium text-gray-900 mb-2">Content Status</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Published</span>
                                    <span className="text-sm font-medium text-green-600">{stats.publishedPosts}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Drafts</span>
                                    <span className="text-sm font-medium text-yellow-600">{stats.draftPosts}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Archived</span>
                                    <span className="text-sm font-medium text-gray-600">{stats.archivedPosts}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Featured</span>
                                    <span className="text-sm font-medium text-purple-600">{stats.featuredPosts}</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-medium text-gray-900 mb-2">Organization</h4>
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Categories</span>
                                    <span className="text-sm font-medium text-indigo-600">{stats.totalCategories}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Tags</span>
                                    <span className="text-sm font-medium text-pink-600">{stats.totalTags}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-gray-600">Total Views</span>
                                    <span className="text-sm font-medium text-blue-600">{stats.totalViews.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BlogDashboard;
